/**
 * UNKWN Cart Helper v5
 * Provides consistent add-to-cart experience with proper visual feedback
 * Handles: bundle-showcase, bundle-upsell, bundle-card, bundle-add-to-cart buttons
 * v4: Added conversion tracking analytics (GA4, Facebook Pixel, Shopify)
 * v5: Performance optimizations (debounced mutations, WeakSet tracking)
 */
window.UNKWNCart = {
  // Performance: Track initialized elements without DOM queries
  _initializedElements: new WeakSet(),
  _initializedForms: new WeakSet(),
  _debounceTimer: null,
  _pendingNodes: [],

  /**
   * Track add-to-cart events across multiple analytics platforms
   * Non-blocking - fires events asynchronously and gracefully handles missing scripts
   * 
   * @param {Object} data - Event data
   * @param {string|number} data.variantId - The variant ID added to cart
   * @param {number} data.quantity - Quantity added
   * @param {number} data.price - Unit price in dollars
   * @param {string} data.productTitle - Product title (optional)
   * @param {string} data.currency - Currency code (default: USD)
   */
  trackAddToCart(data) {
    const { variantId, quantity = 1, price = 0, productTitle = '', currency = 'USD' } = data;
    const value = price * quantity;

    // Fire all analytics events asynchronously (non-blocking)
    setTimeout(() => {
      // 1. Google Analytics 4 (gtag)
      if (typeof gtag === 'function') {
        try {
          gtag('event', 'add_to_cart', {
            currency: currency,
            value: value,
            items: [{
              item_id: String(variantId),
              item_name: productTitle,
              quantity: quantity,
              price: price
            }]
          });
          console.log('[UNKWN Analytics] GA4 add_to_cart fired:', { variantId, value, currency });
        } catch (e) {
          console.warn('[UNKWN Analytics] GA4 tracking error:', e);
        }
      }

      // 2. Facebook Pixel (fbq)
      if (typeof fbq === 'function') {
        try {
          fbq('track', 'AddToCart', {
            content_ids: [String(variantId)],
            content_name: productTitle,
            content_type: 'product',
            value: value,
            currency: currency,
            num_items: quantity
          });
          console.log('[UNKWN Analytics] Facebook Pixel AddToCart fired:', { variantId, value, currency });
        } catch (e) {
          console.warn('[UNKWN Analytics] Facebook Pixel tracking error:', e);
        }
      }

      // 3. Shopify Native Analytics (if available)
      if (window.ShopifyAnalytics && typeof window.ShopifyAnalytics.lib === 'object') {
        try {
          if (typeof window.ShopifyAnalytics.lib.track === 'function') {
            window.ShopifyAnalytics.lib.track('Added Product', {
              variantId: String(variantId),
              productTitle: productTitle,
              quantity: quantity,
              price: price,
              currency: currency
            });
            console.log('[UNKWN Analytics] Shopify Analytics tracked:', { variantId, productTitle });
          }
        } catch (e) {
          console.warn('[UNKWN Analytics] Shopify Analytics tracking error:', e);
        }
      }

      // 4. Trekkie (Shopify's internal analytics)
      if (window.trekkie && typeof window.trekkie.track === 'function') {
        try {
          window.trekkie.track('Added Product', {
            variantId: String(variantId),
            productTitle: productTitle,
            quantity: quantity,
            price: price
          });
          console.log('[UNKWN Analytics] Trekkie tracked:', { variantId });
        } catch (e) {
          console.warn('[UNKWN Analytics] Trekkie tracking error:', e);
        }
      }

      // 5. Dispatch custom event for other integrations
      try {
        window.dispatchEvent(new CustomEvent('unkwn:add_to_cart', {
          detail: {
            variantId: variantId,
            quantity: quantity,
            price: price,
            value: value,
            productTitle: productTitle,
            currency: currency,
            timestamp: Date.now()
          }
        }));
      } catch (e) {
        console.warn('[UNKWN Analytics] Custom event dispatch error:', e);
      }
    }, 0);
  },

  /**
   * Extract product data from the API response for analytics
   * @param {Object} cartResponse - Response from Shopify /cart/add.js
   * @returns {Object} Extracted product data
   */
  extractProductData(cartResponse) {
    // Handle both single item and items array response formats
    const item = cartResponse.items ? cartResponse.items[0] : cartResponse;
    
    return {
      variantId: item.variant_id || item.id,
      quantity: item.quantity || 1,
      price: item.price ? item.price / 100 : 0, // Shopify returns price in cents
      productTitle: item.product_title || item.title || '',
      currency: window.Shopify?.currency?.active || 'USD'
    };
  },

  async addToCart(variantId, quantity = 1, button) {
    const originalHTML = button.innerHTML;

    // 1. Visual feedback - loading state
    button.disabled = true;
    button.classList.add('is-loading');
    button.innerHTML = '<span class="loading-spinner"></span><span>Adding...</span>';

    try {
      // NEW: Check if this is a bundle with components
      const bundleComponents = button.dataset.bundleComponents;
      let items = [];

      if (bundleComponents) {
        // This is a bundle - parse component IDs
        try {
          const componentIds = JSON.parse(bundleComponents);
          items = componentIds.map(id => ({
            id: parseInt(id),
            quantity: 1
          }));
          console.log('[UNKWN Bundle] Adding itemized bundle:', items.length, 'items');
        } catch (e) {
          console.error('[UNKWN Bundle] Failed to parse bundle components:', e);
          // Fallback to single item
          items = [{ id: parseInt(variantId), quantity }];
        }
      } else {
        // Regular product - single item
        items = [{ id: parseInt(variantId), quantity }];
      }

      // 2. Add to cart via Shopify AJAX API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) throw new Error('Failed to add to cart');
      const data = await response.json();

      // 3. Track analytics event (non-blocking)
      // For bundles, track each component
      if (bundleComponents && data.items) {
        data.items.forEach(item => {
          const productData = this.extractProductData({ ...data, items: [item] });
          this.trackAddToCart(productData);
        });
      } else {
        const productData = this.extractProductData(data);
        this.trackAddToCart(productData);
      }

      // 4. Success state with animation
      button.classList.remove('is-loading');
      button.classList.add('is-success');
      const itemCount = items.length;
      const successMessage = itemCount > 1
        ? `Added ${itemCount} Items to Cart!`
        : 'Added to Cart!';
      button.innerHTML = `<svg class="checkmark" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg><span>${successMessage}</span>`;

      // 5. Update cart bubble count
      await this.updateCartBubble();

      // 6. Show cart notification or drawer
      this.showCartFeedback(data);

      // 7. Reset button after delay
      setTimeout(() => {
        button.innerHTML = originalHTML;
        button.disabled = false;
        button.classList.remove('is-success');
      }, 2500);

    } catch (error) {
      console.error('Add to cart error:', error);
      button.classList.remove('is-loading');
      button.classList.add('is-error');
      button.innerHTML = '<span>Error - Try Again</span>';

      setTimeout(() => {
        button.innerHTML = originalHTML;
        button.disabled = false;
        button.classList.remove('is-error');
      }, 2000);
    }
  },

  async updateCartBubble() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update all cart count elements
      document.querySelectorAll('.cart-count-bubble, [data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.classList.toggle('hidden', cart.item_count === 0);
        if (cart.item_count > 0) {
          el.removeAttribute('hidden');
        }
      });

      // Animate the cart icon
      const cartIcon = document.querySelector('.header__icon--cart, [data-cart-icon]');
      if (cartIcon) {
        cartIcon.classList.add('cart-icon-shake');
        setTimeout(() => cartIcon.classList.remove('cart-icon-shake'), 500);
      }
    } catch (e) {
      console.error('Failed to update cart bubble:', e);
    }
  },

  showCartFeedback(cartData) {
    // Try cart notification first (theme setting)
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification) {
      const sectionsToFetch = 'cart-notification-product,cart-notification-button,cart-icon-bubble';
      fetch(window.location.pathname + '?sections=' + sectionsToFetch)
        .then(r => r.json())
        .then(sections => {
          if (cartNotification.renderContents && typeof cartNotification.renderContents === 'function') {
            cartNotification.renderContents({ sections: sections, key: cartData.key || cartData.items?.[0]?.key });
          } else {
            const notification = document.getElementById('cart-notification');
            if (notification) {
              notification.classList.add('animate', 'active');
              setTimeout(() => notification.classList.remove('active'), 5000);
            }
          }
        })
        .catch(() => {
          const notification = document.getElementById('cart-notification');
          if (notification) {
            notification.classList.add('animate', 'active');
            setTimeout(() => notification.classList.remove('active'), 5000);
          }
        });
      return;
    }

    // Fall back to cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      setTimeout(() => cartDrawer.open(), 300);
    }
  },

  // Helper to attach click handler to a button (uses WeakSet for O(1) lookup)
  attachHandler(button) {
    if (this._initializedElements.has(button)) return;
    this._initializedElements.add(button);
    button.addEventListener('click', (e) => {
      e.preventDefault();
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;
      window.UNKWNCart.addToCart(variantId, 1, button);
    });
  },

  // Initialize all UNKWN add-to-cart buttons
  init() {
    // All button selectors that need cart handling
    const selectors = [
      '[data-bundle-showcase-add]',
      '[data-bundle-upsell-add]',
      '[data-bundle-card-add]',
      '[data-bundle-add-to-cart]',
      '[data-upsell-add]'
    ];

    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(button => {
        this.attachHandler(button);
      });
    });

    // Handle product-form elements when product-form.js is not loaded
    // This fixes ADD TO CART buttons in bundle grids on homepage
    if (customElements.get('product-form') === undefined) {
      document.querySelectorAll('product-form form[action*="/cart/add"]').forEach(form => {
        if (this._initializedForms.has(form)) return;
        this._initializedForms.add(form);

        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const button = form.querySelector('button[type="submit"]');
          const variantInput = form.querySelector('input[name="id"]');
          const variantId = variantInput ? variantInput.value : null;

          if (!variantId || !button || button.disabled) return;
          window.UNKWNCart.addToCart(variantId, 1, button);
        });
      });
    }
  },

  // Initialize within a specific container (for targeted updates)
  initWithin(container) {
    if (!container || container.nodeType !== Node.ELEMENT_NODE) return;

    const selectors = [
      '[data-bundle-showcase-add]',
      '[data-bundle-upsell-add]',
      '[data-bundle-card-add]',
      '[data-bundle-add-to-cart]',
      '[data-upsell-add]'
    ];

    selectors.forEach(selector => {
      // Check if container itself matches
      if (container.matches && container.matches(selector)) {
        this.attachHandler(container);
      }
      // Check descendants
      container.querySelectorAll(selector).forEach(button => {
        this.attachHandler(button);
      });
    });

    // Handle product-form within container
    if (customElements.get('product-form') === undefined) {
      if (container.matches && container.matches('product-form')) {
        const form = container.querySelector('form[action*="/cart/add"]');
        if (form && !this._initializedForms.has(form)) {
          this._initializedForms.add(form);
          form.addEventListener('submit', (e) => {
            e.preventDefault();
            const button = form.querySelector('button[type="submit"]');
            const variantInput = form.querySelector('input[name="id"]');
            const variantId = variantInput ? variantInput.value : null;
            if (!variantId || !button || button.disabled) return;
            window.UNKWNCart.addToCart(variantId, 1, button);
          });
        }
      }
      container.querySelectorAll('product-form form[action*="/cart/add"]').forEach(form => {
        if (this._initializedForms.has(form)) return;
        this._initializedForms.add(form);
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const button = form.querySelector('button[type="submit"]');
          const variantInput = form.querySelector('input[name="id"]');
          const variantId = variantInput ? variantInput.value : null;
          if (!variantId || !button || button.disabled) return;
          window.UNKWNCart.addToCart(variantId, 1, button);
        });
      });
    }
  },

  // Debounced mutation handler
  handleMutations(mutations) {
    // Collect added nodes
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          this._pendingNodes.push(node);
        }
      });
    });

    // Debounce processing (100ms)
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      const nodes = this._pendingNodes.splice(0);
      nodes.forEach(node => this.initWithin(node));
    }, 100);
  }
};

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => window.UNKWNCart.init());

// Optimized MutationObserver: Only processes added nodes, debounced
const observer = new MutationObserver((mutations) => window.UNKWNCart.handleMutations(mutations));
observer.observe(document.body, { childList: true, subtree: true });
